import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PageState } from '../component/util/ItemTablePaginated';

// Re-export the AlertType so consumers of the store can use it
export type AlertType = Error | string;

export interface GlobalUIState {
  errors: AlertType[];
  navBarOpen: boolean;
  hasNewVersion: boolean;
  userBrowserTimezone: string;
  fanControllersPage: PageState;
  hubsPage: PageState;
  accountsPage: PageState;
}

export interface GlobalUIActions {
  appendErrors: (errors: AlertType[]) => void;
  resetErrors: () => void;
  setNavBarOpen: (open: boolean) => void;
  setHasNewVersion: (hasNewVersion: boolean) => void;
  setUserBrowserTimezone: (timezone: string) => void;
  setFanControllersPage: (page: PageState) => void;
  setHubsPage: (page: PageState) => void;
  setAccountsPage: (page: PageState) => void;
  reset: () => void;
}

const initialState: GlobalUIState = {
  errors: [],
  navBarOpen: false,
  hasNewVersion: false,
  userBrowserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  fanControllersPage: {
    offset: 0,
    limit: 100,
    sortBy: 'fan_controller_id_next',
    sortAsc: true,
    page: 1,
  },
  hubsPage: {
    offset: 0,
    limit: 100,
    sortBy: 'core_id',
    sortAsc: true,
    page: 1,
  },
  accountsPage: {
    offset: 0,
    limit: 100,
    sortBy: 'account_id',
    sortAsc: true,
    page: 1,
  },
};

/**
 * Global UI state store — holds layout-level ephemeral state that
 * persists across all navigation (never resets).
 *
 * Connected to Redux DevTools via the `devtools` middleware for
 * time-travel debugging and state inspection in development.
 * The middleware no-ops in production builds (devtools extension not present).
 */
export const useGlobalUIStore = create<GlobalUIState & GlobalUIActions>()(
  devtools(
    (set) => ({
      ...initialState,
      appendErrors: (errors) => {
        set((state) => ({ errors: [...state.errors, ...errors] }), undefined, 'appendErrors');
      },
      resetErrors: () => {
        set({ errors: [] }, undefined, 'resetErrors');
      },
      setNavBarOpen: (open) => {
        set({ navBarOpen: open }, undefined, 'setNavBarOpen');
      },
      setHasNewVersion: (hasNewVersion) => {
        set({ hasNewVersion }, undefined, 'setHasNewVersion');
      },
      setUserBrowserTimezone: (timezone) => {
        set({ userBrowserTimezone: timezone }, undefined, 'setUserBrowserTimezone');
      },
      setFanControllersPage: (page) => {
        set({ fanControllersPage: page }, undefined, 'setFanControllersPage');
      },
      setHubsPage: (page) => {
        set({ hubsPage: page }, undefined, 'setHubsPage');
      },
      setAccountsPage: (page) => {
        set({ accountsPage: page }, undefined, 'setAccountsPage');
      },
      reset: () => {
        set(initialState, undefined, 'reset');
      },
    }),
    { name: 'GlobalUI' }
  )
);
